import { type FormEvent, useState } from 'react';
import { useNavigate, useLocation, Navigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ThemeSwitcher } from '@/components/ThemeSwitcher';
import { useAuthStore, type SubjectKind } from '@/stores/auth-store';

export function LoginPage({ subjectKind = 'admin' }: { subjectKind?: SubjectKind }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isPartner = subjectKind === 'partner';
  const home = isPartner ? '/partner' : '/';
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated && s.subjectKind === subjectKind);
  const isLoading = useAuthStore((s) => s.isLoading);
  const login = useAuthStore((s) => s.login);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [needsMfa, setNeedsMfa] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (isAuthenticated) {
    const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? home;
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const result = await login(username, password, needsMfa ? mfaCode : undefined, subjectKind);
      if (result.kind === 'ok') {
        const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? home;
        navigate(from, { replace: true });
        return;
      }
      if (result.kind === 'mfa-required') {
        setNeedsMfa(true);
        setMfaCode('');
        return;
      }
    } catch (err) {
      const e = err as {
        status?: number;
        displayMessage?: string;
        message?: string;
        raw?: { code?: string; detail?: string; title?: string };
      };
      const code = e?.raw?.code ?? '';
      // Surface MFA-specific errors verbatim so ops can see enrollment/code
      // issues. Everything else with a 401 → generic invalid-credentials.
      if (code.startsWith('AUTH-MFA-')) {
        setError(e?.raw?.detail ?? e?.raw?.title ?? e?.displayMessage ?? 'MFA error');
      } else if (e?.status === 401) {
        setError(t('auth.invalidCredentials'));
      } else {
        setError(e?.displayMessage || e?.message || t('auth.invalidCredentials'));
      }
    }
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-muted/30">
      <div className="flex justify-end gap-1 p-4">
        <ThemeSwitcher />
        <LanguageSwitcher />
      </div>
      <div className="flex flex-1 items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="mb-2 flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground font-bold">
                b1
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-sm font-semibold">{t('app.name')}</span>
                <span className="text-xs text-muted-foreground">{t('app.tagline')}</span>
              </div>
            </div>
            <CardTitle>{t(isPartner ? 'partnerPortal.login.title' : 'auth.loginTitle')}</CardTitle>
            <CardDescription>{t(isPartner ? 'partnerPortal.login.subtitle' : 'auth.loginSubtitle')}</CardDescription>
          </CardHeader>
          <form onSubmit={onSubmit}>
            <CardContent className="space-y-4">
              {!needsMfa && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="username">{t('auth.username')}</Label>
                    <Input
                      id="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      autoComplete="username"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">{t('auth.password')}</Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                    />
                  </div>
                </>
              )}
              {needsMfa && (
                <div className="space-y-2">
                  <Label htmlFor="mfaCode">Authenticator code</Label>
                  <Input
                    id="mfaCode"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                    autoComplete="one-time-code"
                    required
                  />
                </div>
              )}
              {error && (
                <p className="text-sm font-medium text-destructive" role="alert">
                  {error}
                </p>
              )}
            </CardContent>
            <CardFooter className="flex-col gap-3">
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
                {isLoading ? t('auth.signingIn') : t('auth.signIn')}
              </Button>
              {isPartner && (
                <p className="text-sm text-muted-foreground">
                  {t('partnerPortal.login.noAccount')}{' '}
                  <Link to="/partner/signup" className="font-medium text-primary underline-offset-4 hover:underline">
                    {t('partnerPortal.login.signupLink')}
                  </Link>
                </p>
              )}
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
